'use client';

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useWorkflowStore } from '../../../stores/workflowStore';
import { MultiColumnSelector } from '../ColumnSelector';
import { useNodeInputColumns } from '../../../hooks/useNodeInputs';
import { Info, Warning } from '@phosphor-icons/react';

type Method = 'fill_down' | 'fill_up' | 'fill_default' | 'merge_expand';

interface NormalizationConfig {
  method?: Method;
  fields?: string[];
  rows?: number;
  value?: string;
}

interface DataNormalizationConfigProps {
  nodeId: string;
  config: NormalizationConfig;
}

export function DataNormalizationConfig({ nodeId, config }: DataNormalizationConfigProps) {
  const { t } = useTranslation();
  const { updateNodeConfig } = useWorkflowStore();
  const inputColumns = useNodeInputColumns(nodeId);

  const [localConfig, setLocalConfig] = useState<NormalizationConfig>({
    method: 'fill_down',
    fields: [],
    rows: 1,
    value: '',
    ...config,
  });

  const [detectionHint, setDetectionHint] = useState<string | null>(null);

  useEffect(() => {
    if (!inputColumns || inputColumns.length === 0) {
      setDetectionHint(null);
      return;
    }
    const possibleGroupFields = inputColumns.filter(({ column }) => {
      const name = column.name.toLowerCase();
      return ['category', 'type', 'group', 'class', 'level', 'name', 'department', 'team'].some(
        (keyword) => name.includes(keyword)
      );
    });
    if (possibleGroupFields.length > 0 && localConfig.method !== 'fill_down') {
      setDetectionHint(
        t('nodes.config.data_normalization.mergeHint', {
          fields: possibleGroupFields.map((f) => f.column.name).join(', '),
        })
      );
    } else {
      setDetectionHint(null);
    }
  }, [inputColumns, localConfig.method, t]);

  const saveConfig = (newConfig: NormalizationConfig) => {
    setLocalConfig(newConfig);
    updateNodeConfig(nodeId, {
      type: 'data_normalization',
      params: { ...newConfig },
    });
  };

  const hasInputConnected = inputColumns && inputColumns.length > 0;
  const method = localConfig.method ?? 'fill_down';
  const needsRows = method === 'fill_down' || method === 'fill_up';
  const needsValue = method === 'fill_default';

  return (
    <div className="space-y-4">
      {/* Auto-detection hint */}
      {detectionHint && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-3 bg-amber-50 rounded-xl border border-amber-200 flex items-start gap-2"
        >
          <Warning className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-700 leading-relaxed">{detectionHint}</p>
        </motion.div>
      )}

      {/* No input connected hint */}
      {!hasInputConnected && (
        <div className="p-3 bg-zinc-50 rounded-xl border border-zinc-200 flex items-start gap-2">
          <Info className="w-4 h-4 text-zinc-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-zinc-500 leading-relaxed">
            {t('nodes.config.data_normalization.connectInputFirst')}
          </p>
        </div>
      )}

      {/* Method dropdown */}
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-zinc-500">
          {t('nodes.config.data_normalization.method')}
        </label>
        <select
          value={method}
          onChange={(e) => saveConfig({ ...localConfig, method: e.target.value as Method })}
          className="w-full px-3 py-2 text-sm bg-white border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
        >
          <option value="fill_down">{t('nodes.config.data_normalization.fill_down')}</option>
          <option value="fill_up">{t('nodes.config.data_normalization.fill_up')}</option>
          <option value="fill_default">{t('nodes.config.data_normalization.fill_default')}</option>
          <option value="merge_expand">{t('nodes.config.data_normalization.merge_expand')}</option>
        </select>
      </div>

      {/* Target fields */}
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-zinc-500">
          {t('nodes.config.data_normalization.targetFields')}
        </label>
        {hasInputConnected ? (
          <MultiColumnSelector
            values={localConfig.fields || []}
            onChange={(fields) => saveConfig({ ...localConfig, fields })}
            columns={inputColumns}
          />
        ) : (
          <div className="px-3 py-2 bg-zinc-100 border border-zinc-200 rounded-lg text-sm text-zinc-400">
            {t('nodes.config.data_normalization.connectInputFirst')}
          </div>
        )}
      </div>

      {/* Row count for fill_down / fill_up */}
      {needsRows && (
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-zinc-500">
            {method === 'fill_down'
              ? t('nodes.config.data_normalization.rowsAbove')
              : t('nodes.config.data_normalization.rowsBelow')}
          </label>
          <input
            type="number"
            min={1}
            value={localConfig.rows ?? 1}
            onChange={(e) => {
              const v = parseInt(e.target.value, 10);
              saveConfig({ ...localConfig, rows: Number.isNaN(v) || v < 1 ? 1 : v });
            }}
            className="w-full px-3 py-2 text-sm bg-white border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
          />
          <p className="text-xs text-zinc-400">
            {method === 'fill_down'
              ? t('nodes.config.data_normalization.rowsAboveHint')
              : t('nodes.config.data_normalization.rowsBelowHint')}
          </p>
        </div>
      )}

      {/* Default value for fill_default */}
      {needsValue && (
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-zinc-500">
            {t('nodes.config.data_normalization.defaultValue')}
          </label>
          <input
            type="text"
            value={localConfig.value || ''}
            onChange={(e) => saveConfig({ ...localConfig, value: e.target.value })}
            placeholder={t('nodes.config.data_normalization.valuePlaceholder')}
            className="w-full text-sm bg-white border border-zinc-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 placeholder:text-zinc-400"
          />
        </div>
      )}

      {/* Method description */}
      <div className="p-3 bg-blue-50 rounded-xl border border-blue-100">
        <div className="flex items-start gap-2">
          <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-blue-600/80 leading-relaxed">
            {t(`nodes.config.data_normalization.${method}Desc`)}
          </p>
        </div>
      </div>
    </div>
  );
}
